// Observer management utilities for efficient intersection and visibility tracking

export class ObserverManager {
  constructor(options = {}) {
    this.callbacks = new Map();
    this.observer = null;
    this.options = options;
  }
  
  observe(element, callback, id) {
    if (!element) return;
    
    if (!this.observer) {
      this.observer = new IntersectionObserver(
        entries => this.handleIntersection(entries),
        this.options
      );
    }
    
    this.callbacks.set(element, { callback, id });
    this.observer.observe(element);
  }
  
  unobserve(element) {
    if (!element || !this.observer) return;
    
    this.observer.unobserve(element);
    this.callbacks.delete(element);
    
    if (this.callbacks.size === 0) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
  
  handleIntersection(entries) {
    entries.forEach(entry => {
      const data = this.callbacks.get(entry.target);
      if (data) data.callback(entry, data.id);
    });
  }
  
  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.callbacks.clear();
  }
}

export class VisibilityManager {
  constructor() {
    this.callbacks = new Map();
    this.isListening = false;
    this.boundHandler = null;
  }
  
  register(callback, id) {
    this.callbacks.set(id, callback);
    
    if (!this.isListening) {
      this.boundHandler = () => this.handleVisibilityChange();
      document.addEventListener('visibilitychange', this.boundHandler, { passive: true });
      this.isListening = true;
    }
  }
  
  unregister(id) {
    this.callbacks.delete(id);
    
    if (this.callbacks.size === 0 && this.isListening) {
      document.removeEventListener('visibilitychange', this.boundHandler);
      this.isListening = false;
      this.boundHandler = null;
    }
  }
  
  handleVisibilityChange() {
    const isVisible = !document.hidden;
    this.callbacks.forEach((callback, id) => {
      callback(isVisible, id);
    });
  }
  
  disconnect() {
    if (this.isListening) {
      document.removeEventListener('visibilitychange', this.boundHandler);
      this.isListening = false;
      this.boundHandler = null;
    }
    this.callbacks.clear();
  }
}

// Create singleton instances for shared use
export const heroIntersectionObserver = new ObserverManager({ threshold: 0.25 });
export const heroVisibilityManager = new VisibilityManager();